import { ComponentType, Fragment } from 'react';
import {
  ArrayField,
  DateField,
  IntegerInputField,
  MultipleChoiceField,
  NumberInputField,
  ObjectField,
  RadioGroupField,
  TextInputField,
  YesNoField
} from '../fields';
import {
  Schema,
  isArraySchema,
  isBooleanSchema,
  isIntegerSchema,
  isNullSchema,
  isNumberSchema,
  isObjectSchema,
  isStringSchema
} from '../types';

import { WidgetMap } from './mapWidget';

/**
 * Figure out which UI widget to render for a given
 * subschema.
 *
 * First, check if a widget has been explicitly set
 * via the $uiOptions and use that.
 * If none is given, do a best-effort guess based on the
 * schema type, format and other schema attributes that
 * might indicate how you want to present it.
 */
export default function resolveFieldOrWidgetComponent(
  schema: Schema
): ComponentType<any> {
  if (schema.$uiOptions && schema.$uiOptions.widget) {
    const { widget } = schema.$uiOptions;
    if (!Object.prototype.hasOwnProperty.call(WidgetMap, widget)) {
      throw new Error(`Unknown widget ${widget}`);
    }

    return WidgetMap[widget];
  }

  if (isNullSchema(schema)) {
    return Fragment;
  }

  if (isArraySchema(schema)) {
    return isStringSchema(schema.items) ? MultipleChoiceField : ArrayField;
  }

  if (isObjectSchema(schema)) {
    return ObjectField;
  }

  if (isBooleanSchema(schema)) {
    return YesNoField;
  }

  if (isStringSchema(schema)) {
    if (schema.enum) {
      return RadioGroupField;
    }

    if (schema.format && schema.format.startsWith('date')) {
      return DateField;
    }
  }

  if (isIntegerSchema(schema)) {
    return IntegerInputField;
  }
  if (isNumberSchema(schema)) {
    return NumberInputField;
  }

  return TextInputField;
}
